// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { MainPanel } from "types";
import { isSet } from "utils";
import { IAction, ActionType, raiseAction, IActionTypeSignUp } from "actions";
import { IState } from "states/states";
import { User } from "states/sitestates";
import { AppState, UserState } from "states/schedulerstates";

import UserPwdChange from "view/comp/UserPwdChange";
import UserPwdForgot from "view/comp/UserPwdForgot";
import SearchableDropdown from "view/comp/SearchableDropdown";

import { ReactComponent as IconUser } from "static/imgUser.svg";
import { IRfid } from "states/ocppstates";

interface IUserProps {
  isRfidUser: boolean;
  rootPath: string | undefined;
  userName: string;
  userEmail: string;
  userList: User[];
  isInitDone: boolean;
  userState: UserState;
  rfid: IRfid | undefined;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const rfid = state.ocpp.rfid.find((curItem) => curItem.rfid === state.site.user?.secId);

  const props: IUserProps = {
    isRfidUser: !!state.site.user?.isRfidUser,
    rootPath: state.router.rootPath,
    userName: curUser?.name ?? state.site.user?.realName ?? "",
    userEmail: state.site.user?.email ?? "",
    userList: state.site.userList ?? [],
    isInitDone: state.scheduler.appState >= AppState.READY,
    userState: state.scheduler.userState,
    rfid,
  };
  return props;
};

interface IUserState {
  selectedUserIndex: number | undefined;
}

interface IUserDispatch {
  onLogoutClick: () => void;
  onChangePasswordClick: () => void;
  onSignUpUser: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserDispatch = {
    onLogoutClick: () => {
      dispatch(raiseAction(ActionType.ACT_LOGOUT_REQ));
    },
    onChangePasswordClick: () => {
      dispatch(raiseAction(ActionType.ACT_CHANGE_PASSWORD));
    },
    onSignUpUser: () => {
      dispatch(raiseAction(ActionType.ACT_SIGNUP));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_select_user: {
    id: "Select user",
  },
});

class UserComponent extends React.Component<
  IUserProps & IUserDispatch & WrappedComponentProps & RoutedProps,
  IUserState
> {
  state: IUserState = {
    selectedUserIndex: undefined,
  };

  public render() {
    return (
      <div id="user">
        <div id="user_inner">
          {this.props.userState !== UserState.CHANGING_PASSWORD && (
            <IconUser width={50} style={{ marginRight: "30px" }} />
          )}
          <div
            id="user_right"
            className={
              this.props.userState !== UserState.CHANGING_PASSWORD &&
              this.props.userState !== UserState.FORGOT_PASSWORD
                ? "legacy"
                : ""
            }
          >
            {this.props.userState === UserState.CHANGING_PASSWORD ? (
              <UserPwdChange showInfoBox={true} />
            ) : this.props.userState === UserState.FORGOT_PASSWORD ? (
              <UserPwdForgot showInfoBox={true} />
            ) : (
              <>
                {this.renderUserInfo()}
                {this.props.userList.length > 1 && this.renderUserAdministration()}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  private renderUserInfo() {
    return (
      <>
        <h1>
          <FormattedMessage id="Current User" />
        </h1>
        <p>
          <FormattedMessage id="The following user is currently logged into this website." />
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                {this.props.isRfidUser ? (
                  <FormattedMessage id="Charging Card" />
                ) : (
                  <FormattedMessage id="Name" />
                )}
                :
              </td>
              <td>{this.props.userName}</td>
            </tr>
            {!this.props.isRfidUser && (
              <tr>
                <td>
                  <FormattedMessage id="Email" />:
                </td>
                <td>{this.props.userEmail}</td>
              </tr>
            )}
            {this.props.isRfidUser && isSet(this.props.rfid?.carName) && (
              <tr>
                <td>
                  <FormattedMessage id="Vehicle" />:
                </td>
                <td>
                  {this.props.rfid.carName}
                  {isSet(this.props.rfid.carBattery) && <> ({this.props.rfid.carBattery} kWh)</>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="buttonbox">
          <Button variant="secondary" id="logout" type="submit" onClick={this.props.onLogoutClick}>
            <FormattedMessage id="Logout" />
          </Button>
          {this.props.isRfidUser ? (
            <Button
              variant="secondary"
              id="changeRfidUserSettings"
              onClick={() =>
                this.props.navigate(
                  `${this.props.rootPath ? `/${this.props.rootPath}` : ".."}/${
                    MainPanel.USER_SETTINGS
                  }`,
                )
              }
            >
              <FormattedMessage id="Settings" />
            </Button>
          ) : (
            <Button
              id="changePassword"
              variant="secondary"
              type="submit"
              onClick={this.props.onChangePasswordClick}
            >
              <FormattedMessage id="Change Password" />
            </Button>
          )}
        </div>
      </>
    );
  }

  private renderUserAdministration() {
    const userIds = Array.from(Array(this.props.userList.length).keys()).sort((a, b) =>
      this.props.userList[a].name.localeCompare(this.props.userList[b].name),
    );

    return (
      <>
        <h1>
          <FormattedMessage id="User Administration" />
        </h1>
        <p>
          <FormattedMessage id="You have the access right to administrate other users." />{" "}
        </p>
        <p>
          <FormattedMessage id="Please select the user to modify in the drop-down box below and press modify afterwards." />
        </p>

        <div className="buttonbar">
          <SearchableDropdown
            caption={this.props.intl.formatMessage(messages.msg_select_user)}
            className="me-2"
            valueList={userIds}
            mapperFunction={(value: number | undefined) => {
              this.setState({ selectedUserIndex: value });
            }}
            labelFunction={(index: number) => this.props.userList[index].name}
            curItem={
              isSet(this.state.selectedUserIndex)
                ? this.props.userList[this.state.selectedUserIndex].name
                : this.props.intl.formatMessage(messages.msg_select_user)
            }
            nextElementId="modifyUser"
          />
          <Button
            variant="secondary"
            id="modifyUser"
            disabled={this.state.selectedUserIndex === undefined}
            onClick={() =>
              this.props.navigate(
                `../${MainPanel.USER_SETTINGS}?user=${
                  this.props.userList[this.state.selectedUserIndex!]?.id
                }`,
              )
            }
          >
            <FormattedMessage id="Modify" />
          </Button>
        </div>
        <p className="mt-4">
          <FormattedMessage id="To add a new user press the create button below." />
        </p>
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              this.props.onSignUpUser();
              this.props.navigate(`../${MainPanel.USER_SETTINGS}`);
            }}
          >
            <FormattedMessage id="Create" />
          </Button>
        </div>
      </>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withRouter(UserComponent)));
